import { useCallback, useEffect, useRef, useState } from "react";

import { useRouter } from "next/router";

import { fileToBase64 } from "@/common/utils/file.utils";
import {
  convertHeicToPng,
  resizeImage,
  resizeImageToFit,
} from "@/common/utils/image.utils";
import { useDisplayCurrency } from "@/features/display-currency/useDisplayCurrency";
import type {
  ValueCheckFailure,
  ValueCheckResult,
} from "@/features/value-check/model";
import {
  readValueCheckShown,
  saveValueCheckShown,
  saveValueCheckStash,
} from "@/features/value-check/utils/value-check-stash";
import {
  VALUE_CHECK_API_ROUTE,
  VALUE_CHECK_MAX_PHOTO_BYTES,
} from "@/features/value-check/value-check.static";

export type ValueCheckPhase =
  | { name: "idle" }
  | { name: "checking"; photoUrl: string }
  | { name: "done"; photoUrl: string; result: ValueCheckResult }
  | { name: "failed"; photoUrl: string | null; failure: ValueCheckFailure };

const UNAVAILABLE: ValueCheckFailure = { reason: "unavailable" };

/**
 * Set while this tab has an answer on screen. A reload or "back" from create-ad keeps it (F3); a
 * new visit starts on the landing with "Your last check" instead (WHO-6534 F34).
 */
const SHOWN_IN_VISIT_KEY = "value-check:shown-in-visit";

const markShownInVisit = (shown: boolean) => {
  try {
    if (shown) {
      window.sessionStorage.setItem(SHOWN_IN_VISIT_KEY, "1");
    } else {
      window.sessionStorage.removeItem(SHOWN_IN_VISIT_KEY);
    }
  } catch {
    // Storage blocked: every visit starts on the landing.
  }
};

const shownInVisit = () => {
  try {
    return window.sessionStorage.getItem(SHOWN_IN_VISIT_KEY) === "1";
  } catch {
    return false;
  }
};

/** The answer kept for "Your last check" on the landing. */
export type ValueCheckLast = { photoUrl: string; result: ValueCheckResult };
const UNREADABLE: ValueCheckFailure = { reason: "photo" };

/** HEIC to PNG, resized like create-ad's photos, then under the engine's size limit. */
const preparePhoto = async (file: File): Promise<File | null> =>
  resizeImageToFit(
    await resizeImage(await convertHeicToPng(file)),
    VALUE_CHECK_MAX_PHOTO_BYTES
  );

const readFailure = async (response: Response): Promise<ValueCheckFailure> => {
  const body = (await response
    .json()
    .catch(() => null)) as ValueCheckFailure | null;

  return body?.reason ? body : UNAVAILABLE;
};

/**
 * One value check from the browser: prepare the photo, send it with the Turnstile header, keep
 * the answer in this browser for "Create ad". A new photo starts over.
 */
export const useValueCheck = ({
  getHeaders,
}: {
  /** Turnstile headers (`useTurnstile().getHeaders`). */
  getHeaders: () => Promise<Record<string, string>>;
}) => {
  const { locale } = useRouter();
  const currency = useDisplayCurrency();
  const [phase, setPhase] = useState<ValueCheckPhase>({ name: "idle" });
  const [last, setLast] = useState<ValueCheckLast | null>(null);
  const photoUrlRef = useRef<string | null>(null);

  const replacePhotoUrl = useCallback((file: Blob | null) => {
    if (photoUrlRef.current) {
      URL.revokeObjectURL(photoUrlRef.current);
    }

    photoUrlRef.current = file ? URL.createObjectURL(file) : null;

    return photoUrlRef.current;
  }, []);

  // The last check keeps its own object URL: a new check revokes the one on screen.
  const lastUrlRef = useRef<string | null>(null);

  const keepLast = useCallback((photo: Blob, result: ValueCheckResult) => {
    if (lastUrlRef.current) {
      URL.revokeObjectURL(lastUrlRef.current);
    }

    lastUrlRef.current = URL.createObjectURL(photo);
    const kept = { photoUrl: lastUrlRef.current, result };
    setLast(kept);

    return kept;
  }, []);

  useEffect(
    () => () => {
      replacePhotoUrl(null);

      if (lastUrlRef.current) {
        URL.revokeObjectURL(lastUrlRef.current);
      }
    },
    [replacePhotoUrl]
  );

  // A reload or "back" from create-ad shows the last answer again, so a visitor does not spend
  // another of their checks on the same photo (WHO-6534 F3). A new visit offers it on the landing.
  useEffect(() => {
    let cancelled = false;

    void readValueCheckShown().then(shown => {
      if (!shown || cancelled) {
        return;
      }

      keepLast(shown.photo, shown.result);

      if (shownInVisit()) {
        const photoUrl = replacePhotoUrl(shown.photo) as string;
        setPhase(current =>
          current.name === "idle"
            ? { name: "done", photoUrl, result: shown.result }
            : current
        );
      }
    });

    return () => {
      cancelled = true;
    };
  }, [keepLast, replacePhotoUrl]);

  const check = useCallback(
    async (file: File) => {
      const photo = await preparePhoto(file).catch(() => null);

      if (!photo) {
        setPhase({
          name: "failed",
          photoUrl: replacePhotoUrl(null),
          failure: UNREADABLE,
        });

        return;
      }

      const photoUrl = replacePhotoUrl(photo) as string;
      setPhase({ name: "checking", photoUrl });

      try {
        const response = await fetch(VALUE_CHECK_API_ROUTE, {
          method: "POST",
          headers: {
            ...(await getHeaders()),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            photo: await fileToBase64(photo),
            locale,
            currency: currency ?? null,
          }),
        });

        if (!response.ok) {
          setPhase({
            name: "failed",
            photoUrl,
            failure: await readFailure(response),
          });

          return;
        }

        const result = (await response.json()) as ValueCheckResult;

        await saveValueCheckStash({
          photo,
          title: result.title,
          labels: result.labels,
          savedAt: Date.now(),
        });
        await saveValueCheckShown({ photo, result, savedAt: Date.now() });
        markShownInVisit(true);
        keepLast(photo, result);
        setPhase({ name: "done", photoUrl, result });
      } catch {
        setPhase({ name: "failed", photoUrl, failure: UNAVAILABLE });
      }
    },
    [currency, getHeaders, keepLast, locale, replacePhotoUrl]
  );

  /** Back to the landing; the answer stays there as "Your last check". */
  const reset = useCallback(() => {
    markShownInVisit(false);
    setPhase({ name: "idle" });
  }, []);

  const openLast = useCallback(() => {
    if (last) {
      markShownInVisit(true);
      setPhase({ name: "done", photoUrl: last.photoUrl, result: last.result });
    }
  }, [last]);

  return { phase, last, check, reset, openLast };
};
